import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAppToast } from "@/shared/composables/useAppToast";
import { usePermissions } from "@/shared/composables/usePermissions";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { useUsersAdminStore } from "../../stores/useUsersAdminStore";
/** BFF error codes (dotted, OPUS-emitted) → i18n keys, resolved via `resolveApiErrorKey`. */
const USER_ACTION_ERROR_KEYS = {
    "User.Deactivation.Self": "accessManager.errors.deactivateSelf",
    "User.Deactivation.LastAccessManager": "accessManager.errors.deactivateLastAccessManager",
    "User.ResendInvitation.NotInvited": "accessManager.errors.resendNotInvited",
    "User.ResendInvitation.Deactivated": "accessManager.errors.resendDeactivated",
    // No User.ResendInvitation.AlreadyMember entry: OPUS no longer refuses a resend for an invitee
    // who is already an organisation member, because since OP-2561 that is the ordinary state of
    // every invitee whose account it creates. It re-sends the account-setup email instead.
};
const ACTION_META = {
    deactivate: {
        run: (s, id) => s.deactivateUser(id),
        icon: "ri-forbid-line",
        labelKey: "accessManager.users.actions.deactivate",
        successKey: "accessManager.toast.deactivated",
        danger: true,
        confirm: {
            titleKey: "accessManager.confirm.deactivate.title",
            bodyKey: "accessManager.confirm.deactivate.body",
            confirmKey: "accessManager.confirm.deactivate.confirm",
            danger: true,
        },
    },
    reactivate: {
        run: (s, id) => s.reactivateUser(id),
        icon: "ri-play-circle-line",
        labelKey: "accessManager.users.actions.reactivate",
        successKey: "accessManager.toast.reactivated",
        confirm: {
            titleKey: "accessManager.confirm.reactivate.title",
            bodyKey: "accessManager.confirm.reactivate.body",
            confirmKey: "accessManager.confirm.reactivate.confirm",
            danger: false,
        },
    },
    resetPassword: {
        run: (s, id) => s.resetPassword(id),
        icon: "ri-lock-password-line",
        labelKey: "accessManager.users.actions.resetPassword",
        successKey: "accessManager.toast.passwordReset",
        confirm: {
            titleKey: "accessManager.confirm.resetPassword.title",
            bodyKey: "accessManager.confirm.resetPassword.body",
            confirmKey: "accessManager.confirm.resetPassword.confirm",
            danger: false,
        },
    },
    resendInvitation: {
        run: (s, id) => s.resendInvitation(id),
        icon: "ri-mail-send-line",
        labelKey: "accessManager.users.actions.resendInvite",
        successKey: "accessManager.toast.inviteResent",
    },
};
/** The actions offered per lifecycle state (see the `Action → route → state` matrix). */
const ACTIONS_BY_STATE = {
    pending: ["resendInvitation"],
    active: ["resetPassword", "deactivate"],
    inactive: ["reactivate"],
};
function resolveState(user) {
    if (user.isInvited)
        return "pending";
    return user.enabled ? "active" : "inactive";
}
/**
 * Centralizes the Lot 4 admin user actions so the list and detail views share one
 * implementation: the per-state action set, the confirm-modal state machine, the
 * success toast, and the BFF errorCode → localized error toast. Permission-gated —
 * `buildActionsFor` returns no items when the caller lacks `accessmanager.manage-users`
 * (the route already blocks non-managers; this is defense-in-depth per §6).
 */
export function useUserActions(options = {}) {
    const { t } = useI18n();
    const store = useUsersAdminStore();
    const { canManageUsers } = usePermissions();
    const { toastSuccess, toastError } = useAppToast();
    const pending = ref(null);
    const isRunning = ref(false);
    async function execute(kind, userId) {
        if (isRunning.value)
            return;
        isRunning.value = true;
        try {
            await ACTION_META[kind].run(store, userId);
            toastSuccess(ACTION_META[kind].successKey);
            await options.onActionSuccess?.(userId);
        }
        catch (error) {
            // User feedback via toast satisfies R46 (no logger needed on a surfaced catch).
            toastError(resolveApiErrorKey(error, USER_ACTION_ERROR_KEYS, "accessManager.errors.actionFailed"));
        }
        finally {
            isRunning.value = false;
            pending.value = null;
        }
    }
    function trigger(kind, userId) {
        if (ACTION_META[kind].confirm) {
            pending.value = { kind, userId };
        }
        else {
            void execute(kind, userId);
        }
    }
    function buildActionsFor(user) {
        if (!canManageUsers.value)
            return [];
        const items = ACTIONS_BY_STATE[resolveState(user)].map((kind) => {
            const meta = ACTION_META[kind];
            return {
                label: t(meta.labelKey),
                icon: meta.icon,
                danger: meta.danger,
                testId: `user-action-${kind}`,
                onClick: () => trigger(kind, user.id),
            };
        });
        // Prepended after the permission gate, so the gate keeps its single exit point.
        // Placement relative to the gate is unobservable today: both module routes require
        // `accessmanager.manage-users`, so a non-manager never renders this view.
        if (options.onEditPermissions) {
            items.unshift({
                label: t("accessManager.users.actions.editPermissions"),
                icon: "ri-shield-user-line",
                testId: "user-action-editPermissions",
                onClick: () => options.onEditPermissions?.(user.id),
            });
        }
        if (options.onShowProperties) {
            items.unshift({
                label: t("accessManager.users.actions.showProperties"),
                icon: "ri-information-line",
                testId: "user-action-showProperties",
                onClick: () => options.onShowProperties?.(user.id),
            });
        }
        return items;
    }
    const pendingConfirm = computed(() => pending.value ? (ACTION_META[pending.value.kind].confirm ?? null) : null);
    return {
        canManageUsers,
        buildActionsFor,
        isConfirmOpen: computed(() => pendingConfirm.value !== null),
        confirmTitleKey: computed(() => pendingConfirm.value?.titleKey ?? ""),
        confirmBodyKey: computed(() => pendingConfirm.value?.bodyKey ?? ""),
        confirmLabelKey: computed(() => pendingConfirm.value?.confirmKey ?? ""),
        confirmDanger: computed(() => pendingConfirm.value?.danger ?? false),
        isRunning,
        confirm: async () => {
            if (pending.value)
                await execute(pending.value.kind, pending.value.userId);
        },
        cancel: () => {
            pending.value = null;
        },
    };
}
